import isEmpty from 'lodash/isEmpty'
import mapKeys from 'lodash/mapKeys'

import { PICTURE_TYPES } from 'Global/constants'
import { getPicturefilePath } from 'Global/Helpers/rugHelpers'
import { set } from 'Global/Helpers/objectHelpers'
import { updateReservations } from 'Global/Helpers/reservationHelpers'

import { SIGN_OUT } from 'Global/Stores/auth'

import { RESERVATION_CREATE_FULFILLED, RESERVATION_DELETE_FULFILLED, RESERVATION_EDIT_FULFILLED } from 'Global/Stores/reservations'

const PICTURE_DESIGNER_UPLOAD = 'API|PICTURE/PICTURE_DESIGNER_UPLOAD'
export const PICTURE_DESIGNER_UPLOAD_FULFILLED = `FULFILLED_${PICTURE_DESIGNER_UPLOAD}`
export const PICTURE_DESIGNER_UPLOAD_PENDING = `PENDING_${PICTURE_DESIGNER_UPLOAD}`
export const PICTURE_DESIGNER_UPLOAD_REJECTED = `REJECTED_${PICTURE_DESIGNER_UPLOAD}`

const PICTURE_PRIMARY_SET = 'API|PICTURE/PRIMARY/SET'
export const PICTURE_PRIMARY_SET_FULFILLED = `FULFILLED_${PICTURE_PRIMARY_SET}`

const REQUEST_CREATE = 'API|REQUEST/CREATE'
export const REQUEST_CREATE_FULFILLED = `FULFILLED_${REQUEST_CREATE}`

const REQUEST_DELETE = 'API|REQUEST/DELETE'
export const REQUEST_DELETE_FULFILLED = `FULFILLED_${REQUEST_DELETE}`

const RUGS_EXPORT_EXCEL = 'API|RUGS/EXPORT/EXCEL'
export const RUGS_EXPORT_EXCEL_FULFILLED = `FULFILLED_${RUGS_EXPORT_EXCEL}`

const RUGS_GET = 'API|RUGS/GET'
const RUGS_GET_FULFILLED = `FULFILLED_${RUGS_GET}`
const RUGS_GET_PENDING = `PENDING_${RUGS_GET}`
const RUGS_GET_REJECTED = `REJECTED_${RUGS_GET}`

const RUG_MFD_TOGGLE = 'API|RUG/MFD/TOGGLE'
export const RUG_MFD_TOGGLE_FULFILLED = `FULFILLED_${RUG_MFD_TOGGLE}`

const RUG_MASTERPIECE_TOGGLE = 'API|RUG/MASTERPIECE/TOGGLE'
export const RUG_MASTERPIECE_TOGGLE_FULFILLED = `FULFILLED_${RUG_MASTERPIECE_TOGGLE}`

/**
 * POST request to the API to upload a new designer picture.
 * @param {Object} data - FormData.
 * @param {number} picture - Picture's id.
 * @returns {Object} Action object.
 */
export const pictureDesignerUpload = (data, picture) => ({
  meta: {
    appendToActions: { picture },
    data,
    errorMessage: `Error uploading designer picture. Please try again.`,
    header: { 'Content-Type': 'multipart/form-data' },
    method: 'post',
    successMessage: `Designer picture uploaded.`,
    url: '/api/v1/fileUpload/designerPicture',
  },
  type: PICTURE_DESIGNER_UPLOAD,
})

/**
 * PATCH request to the API to set a new primary picture.
 * @param {number} picture - Picture's id.
 * @returns {Object} Action object.
 */
export const picturePrimarySet = picture => ({
  payload: {
    confirmationMessage: 'Are you sure you want to set a new primary picture?',
  },
  meta: {
    data: { picture },
    appendToActions: { picture },
    method: 'patch',
    errorMessage: `Error setting new primary picture. Please try again.`,
    successMessage: `New primary picture set.`,
    url: '/api/v1/pictures/setPrimary',
  },
  type: PICTURE_PRIMARY_SET,
})

/**
 * POST request to the API to create a request.
 * @param {number} rug - Rug ID.
 * @param {number} user - User ID.
 * @returns {object} Action object.
 */
export const requestCreate = (rug, user) => ({
  meta: {
    data: { rug, user },
    errorMessage: `Could not create request. Please try again.`,
    method: 'post',
    successMessage: `Request created.`,
    url: '/api/v1/requests',
  },
  type: REQUEST_CREATE,
})

/**
 * DELETE request to the API to delete a request.
 * @param {number} request - Request ID.
 * @param {number} user - User ID.
 * @returns {object} Action object.
 */
export const requestDelete = (request, user) => ({
  meta: {
    data: { request, user },
    errorMessage: `Could not remove request. Please try again.`,
    method: 'delete',
    successMessage: `Request removed.`,
    url: '/api/v1/requests',
  },
  type: REQUEST_DELETE,
})

/**
 * PATCH request to the API to toggle a rugs marked for designers status.
 * @param {number} id - Rug's id.
 * @returns {object} Action object.
 */
export const rugMfdToogle = id => ({
  meta: {
    data: { id },
    errorMessage: `Could not toggle 'Marked for Designers'. Please try again.`,
    method: 'patch',
    successMessage: `Toggled 'Marked for Designers'.`,
    url: '/api/v1/rugs/toggleMarkedForDesigners',
  },
  type: `${RUG_MFD_TOGGLE}`,
})

/**
 * PATCH request to the API to toggle a rugs marked for designers status.
 * @param {number} id - Rug's id.
 * @returns {object} Action object.
 */
export const rugMasterpieceToogle = id => ({
  meta: {
    data: { id },
    errorMessage: `Could not toggle 'Marked as Masterpiece'. Please try again.`,
    method: 'patch',
    successMessage: `Toggled 'Marked as Masterpiece'.`,
    url: '/api/v1/rugs/toggleIsMasterpiece',
  },
  type: `${RUG_MASTERPIECE_TOGGLE}`,
})

/**
 * POST request to the api to export rugs to an excel file.
 * Limited to 1000 rugs.
 * @param {array} rugs - Rugs to export.
 * @returns {object} Action object.
 */
export const rugsExportExcel = rugs => ({
  meta: {
    data: { rugs: rugs.slice(0, 1000) },
    errorMessage: `Error exporting to Excel. Please try again.`,
    method: 'post',
    responseType: 'arraybuffer',
    successMessage: `Exported the first thousand search results.`,
    url: '/api/v1/excel/createFromArray',
  },
  type: RUGS_EXPORT_EXCEL,
})

/**
 * GET request to the API to get all rugs.
 * @returns {object} Action object.
 */
export const rugsGet = () => ({
  meta: { url: '/api/v1/rugs' },
  type: `${RUGS_GET}`,
})

const initialState = {
  data: {},
  done: false,
  error: false,
  pending: false,
}

const reducer = (state = initialState, action) => {
  const { payload, type } = action

  switch (type) {
    case PICTURE_DESIGNER_UPLOAD_FULFILLED:
      if (isEmpty(state.data || !state.data[payload.data.id])) {
        return state
      }

      return set(
        `data.${payload.data.id}`,
        {
          ...state.data[payload.data.id],
          picture: getPicturefilePath(payload.data.pictures.find(p => p.primary), PICTURE_TYPES.C3_THUMBNAIL),
          pictureBig: getPicturefilePath(payload.data.pictures.find(p => p.primary), PICTURE_TYPES.C3_BIG),
          pictureSource: 'designer',
          requests: payload.data.requests.filter(r => !r.deleted && !r.fulfilled),
        },
        state,
      )

    case PICTURE_PRIMARY_SET_FULFILLED:
      if (isEmpty(state.data) || !state.data[payload.data.id]) {
        return state
      }

      const test = {
        ...state,
        data: {
          ...state.data,
          [payload.data.id]: { ...payload.data },
        },
      }

      return test

    case REQUEST_CREATE_FULFILLED:
      if (isEmpty(state.data || !state.data[payload.data.rug])) {
        return state
      }

      return {
        ...state,
        data: {
          ...state.data,
          [payload.data.rug]: {
            ...state.data[payload.data.rug],
            requests: [...state.data[payload.data.rug].requests, payload.data],
          },
        },
      }

    case REQUEST_DELETE_FULFILLED:
      if (isEmpty(state.data || !state.data[payload.data.rug])) {
        return state
      }

      return {
        ...state,
        data: {
          ...state.data,
          [payload.data.rug]: {
            ...state.data[payload.data.rug],
            requests: [...state.data[payload.data.rug].requests.filter(r => r.id !== payload.data.id), payload.data],
          },
        },
      }

    case RESERVATION_CREATE_FULFILLED:
      if (isEmpty(state.data || !state.data[payload.data.rug])) {
        return state
      }

      return set(`data.${payload.data.rug}.reservations`, [...state.data[payload.data.rug].reservations, payload.data], state)

    case RESERVATION_DELETE_FULFILLED:
      if (isEmpty(state.data)) {
        return state
      }

      return set(
        `data.${payload.data.rug}.reservations`,
        updateReservations(state.data[payload.data.rug].reservations, payload.data),
        state,
      )

    case RESERVATION_EDIT_FULFILLED:
      if (isEmpty(state.data)) {
        return state
      }

      return set(
        `data.${payload.data.rug}.reservations`,
        updateReservations(state.data[payload.data.rug].reservations, payload.data),
        state,
      )

    case RUGS_GET_FULFILLED:
      return {
        ...state,
        data: mapKeys(payload.data, 'id'),
        done: true,
        error: false,
        pending: false,
      }

    case RUGS_GET_PENDING:
      return {
        ...state,
        done: false,
        error: false,
        pending: true,
      }

    case RUGS_GET_REJECTED:
      return {
        ...state,
        done: true,
        error: true,
        pending: false,
      }

    case RUG_MASTERPIECE_TOGGLE_FULFILLED:
      if (isEmpty(state.data || !state.data[payload.data])) {
        return state
      }

      return set(`data.${payload.data}.isMasterpiece`, !state.data[payload.data].isMasterpiece, state)

    case RUG_MFD_TOGGLE_FULFILLED:
      if (isEmpty(state.data || !state.data[payload.data])) {
        return state
      }

      return set(`data.${payload.data}.markedForDesigners`, !state.data[payload.data].markedForDesigners, state)

    case SIGN_OUT:
      return initialState

    default:
      return state
  }
}

export default reducer
